import { Chip, Typography } from '@weareopenr/component-library';
import CollapsibleSection from 'src/components/CollapsibleSection';
import { ModalTitle } from 'src/components/ModalTitle/ModalTitle';
import ChannelsSummarySection from 'src/components/PublishModal/SummaryView/ChannelsSummarySection/ChannelsSummarySection';
import VenuesSummarySection from 'src/components/PublishModal/SummaryView/VenuesSummarySection/VenuesSummarySection';
import PublishedTimeSummarySection from 'src/components/PublishModal/SummaryView/PublishedTimeSummarySection/PublishedTimeSummarySection';
import { destinationId } from 'src/components/PublishModal/utilities/destinationId/destinationId';
import { formatDateTimeDayJs } from 'src/helpers/formatDateTime/formatDateTimeDayJs';
import { hasPartialAvailability } from 'src/helpers/sectionAvailability/sectionAvailability';
import { useMenusById } from 'src/hooks/useMenusById';
import { getTrack } from 'src/utils/timetables/helpers';
import { ITimetableSummaryViewProps } from '../types';
import styles from '../../PublishModal/SummaryView/SummaryView.module.scss';

const TimetableSummaryView = ({
  timetableName,
  watch,
  publicationData,
  entityGroups,
}: ITimetableSummaryViewProps): React.ReactElement => {
  const selectedChannelIds: string[] = watch('selectedChannels') ?? [];
  const selectedVenueIds: string[] = watch('venues') ?? [];

  // Menu sets are listed with the menus, as they share the menu track.
  const menus = entityGroups.filter((entity) => getTrack(entity.entityType) === 'menu');
  const priceSchemes = entityGroups.filter((entity) => getTrack(entity.entityType) === 'price_scheme');
  const hasEntityBreakdown = entityGroups.length > 0;

  // Matches `buildPublishSelection`: an instance with none of its venues selected isn't published to.
  const publishedInstanceIds = publicationData
    .filter(
      (destination) =>
        selectedChannelIds.includes(destinationId(destination)) &&
        destination.venues.some((venue) => selectedVenueIds.includes(venue.venue_id)),
    )
    .map(destinationId);

  // Only menus have sections to check - a menu set's id isn't a menu id.
  const { data: menuDetails } = useMenusById(
    menus.filter((menu) => menu.entityType === 'menu').map((menu) => menu.entityId),
  );
  const partiallyAvailableMenuIds = new Set(
    menuDetails
      .filter((menu) => hasPartialAvailability(menu.sections, publishedInstanceIds))
      .map((menu) => menu.menuId),
  );

  const formValues = watch();

  return (
    <section>
      <ModalTitle title={`Summary for ${formValues.scheduleName || timetableName}`} />
      <div className={styles['sections']}>
        <CollapsibleSection
          number="1"
          title="Timetable"
          subtitle={`${timetableName}. Snapshots taken at ${formatDateTimeDayJs(new Date())}.`}
          defaultExpanded={hasEntityBreakdown}
        >
          {hasEntityBreakdown && (
            <div className={styles['channel-container']}>
              <div>
                <Typography weight="semi-bold">Menus in timetable:</Typography>
                <div className={styles['entity-list']}>
                  {menus.map((menu) => (
                    <div key={menu.entityId} data-testid={`summary-menu-${menu.entityId}`}>
                      <div className={styles['entity-name']}>
                        <Typography weight="semi-bold">{menu.name}</Typography>
                        {partiallyAvailableMenuIds.has(menu.entityId) && (
                          <Chip label="Partial Availability" backgroundColour="grape" />
                        )}
                      </div>
                      <Typography size="small" className={styles['muted']}>
                        {menu.daysLabel}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Typography weight="semi-bold">Price Schemes in timetable:</Typography>
                <div className={styles['entity-list']}>
                  {priceSchemes.map((priceScheme) => (
                    <div key={priceScheme.entityId} data-testid={`summary-price-scheme-${priceScheme.entityId}`}>
                      <Typography weight="semi-bold">{priceScheme.name}</Typography>
                      <Typography size="small" className={styles['muted']}>
                        {priceScheme.daysLabel}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CollapsibleSection>
        <ChannelsSummarySection number="2" publicationData={publicationData} selectedChannelIds={selectedChannelIds} />
        <VenuesSummarySection
          number="3"
          publicationData={publicationData}
          selectedChannelIds={selectedChannelIds}
          selectedVenueIds={selectedVenueIds}
        />
        <PublishedTimeSummarySection
          number="4"
          publicationDate={formValues.publicationDate}
          publicationType={formValues.publicationType}
        />
      </div>
    </section>
  );
};

export default TimetableSummaryView;
